import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { GlobalService, parseApiErrors } from "../../modules/shared/services";
import i18n from "../../i18n";

// Interface for category object from backend
export interface CategoryObject {
    id: string;
    name: string;
    description: string;
    subcategories: string[];
    translatedName?: Record<string, string>; // Translations: { it: "Nome italiano", es: "Nombre español", ... }
    translatedSubCategories?: Record<string, string[]>; // Translations: { it: ["Sottocategoria1", ...], es: [...], ... }
    type: {
        id: string;
    };
    isEmergency: boolean;
    isRequireVerification: boolean;
    requirements: string[];
    createdAt: string;
    updatedAt: string;
}

// Interface for the user category data
export interface UserCategoryData {
    type: {
        id: number;
    };
    userId: {
        id: string;
    };
    categoryIds: CategoryObject[]; // Full category objects, not just IDs
    subCategories: string[];
    serviceAreaIds?: { id: string }[];
}

// Interface for the response
export interface UserCategoryResponse {
    id: string;
    type: {
        id: number;
    };
    userId: {
        id: string;
    };
    categoryIds: CategoryObject[]; // Full category objects, not just IDs
    subCategories: string[];
    serviceAreaIds?: any[]; // Full service area objects from backend
    createdAt: string;
    updatedAt: string;
}

// Fetch user categories by userId and type
export const getUserCategoriesByType = createAsyncThunk<
    UserCategoryResponse | null,
    { userId: string; type: number }
>(
    "userCategory/getUserCategoriesByType",
    async ({ userId, type }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.get(
                `api/v1/user-categories/user/${userId}/type/${type}`,
                true,
                {}
            );
            
            dispatch(hideLoader());
            
            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Create new user category selection
export const createUserCategories = createAsyncThunk<
    UserCategoryResponse,
    UserCategoryData
>(
    "userCategory/createUserCategories",
    async (data, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.post(
                "api/v1/user-categories",
                data,
                { "Content-Type": "application/json" },
                true
            );
            
            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.selectionsSaved"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Update existing user category selection
export const updateUserCategories = createAsyncThunk<
    UserCategoryResponse,
    { id: string; data: UserCategoryData }
>(
    "userCategory/updateUserCategories",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.patch(
                `api/v1/user-categories/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );
            
            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.selectionsUpdated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Save or update user categories (smart function that handles both create and update)
export const saveUserCategories = createAsyncThunk<
    UserCategoryResponse,
    { userId: string; type: number; categoryIds: Array<{ id: string }>; subCategories: string[]; serviceAreaIds?: Array<{ id: string }> }
>(
    "userCategory/saveUserCategories",
    async ({ userId, type, categoryIds, subCategories, serviceAreaIds }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            // First, try to fetch existing record
            const existingRecord = await HttpService.get(
                `api/v1/user-categories/user/${userId}/type/${type}`,
                true,
                {}
            );
            
            // Use minimal format for both PATCH and POST requests
            const requestData = {
                type: { id: type },
                userId: { id: userId },
                categoryIds: categoryIds, // Already in correct format { id: string }[]
                subCategories: subCategories,
                serviceAreaIds: serviceAreaIds || [] // Include service area IDs
            };
            
            console.log("Sending data to backend:", requestData);
            
            let response;
            
            if (existingRecord && existingRecord.id) {
                // Update existing record - use minimal format
                console.log("Sending PATCH data:", requestData);
                response = await HttpService.patch(
                    `api/v1/user-categories/${existingRecord.id}`,
                    requestData,
                    { "Content-Type": "application/json" },
                    true
                );
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.selectionsUpdated"));
            } else {
                // Create new record - use minimal format
                console.log("Sending POST data:", requestData);
                response = await HttpService.post(
                    "api/v1/user-categories",
                    requestData,
                    { "Content-Type": "application/json" },
                    true
                );
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.selectionsSaved"));
            }
            
            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Fetch user categories by userId (without type) - returns full record(s)
export const getUserCategoriesByUser = createAsyncThunk<
    any,
    { userId: string }
>(
    "userCategory/getUserCategoriesByUser",
    async ({ userId }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/user-categories/user/${userId}`,
                true,
                {}
            );

            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
